/**
 * Pagination dots for the "Text columns with images" grid-slider carousel.
 *
 * The theme's native <grid-slider> web component (theme.js) only builds
 * prev/next arrows — it has no dots/pagination support at all. This script
 * adds that on top, without touching theme.js.
 *
 * Scoping: section-columns.liquid now has a "Slider navigation" setting
 * (Arrows / Dots). When "Dots" is chosen, multicolumn.liquid renders
 * <grid-slider data-navigation="dots">, hides the native arrows via CSS,
 * and this script builds dots for exactly that instance — any section left
 * on "Arrows" is untouched. No section needs the "review-blocks" class for
 * this to work anymore.
 *
 * HOW TO USE:
 * 1. Upload this file to Assets as, e.g., "review-slider-dots.js".
 * 2. Load it once on every page, deferred, right before </body> in
 *    layout/theme.liquid:
 *      {{ 'review-slider-dots.js' | asset_url | script_tag }}
 *    (or any equivalent <script defer src="..."> tag you already use to
 *    include custom assets.)
 */
(function () {
  'use strict';

  const WRAPPER_SELECTOR = 'grid-slider[data-navigation="dots"]';
  const SLIDER_SELECTOR = '[data-grid-slider]';
  const SLIDE_SELECTOR = '[data-grid-item]';

  const CLASSES = {
    dots: 'review-slider-dots',
    dot: 'review-slider-dots__dot',
    active: 'is-active',
  };

  let stylesInjected = false;

  function injectStyles() {
    if (stylesInjected) return;
    stylesInjected = true;

    const style = document.createElement('style');
    style.textContent = `
      .${CLASSES.dots} {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 8px;
        margin-top: 16px;
      }

      .${CLASSES.dot} {
        appearance: none;
        border: none;
        background: rgba(0, 0, 0, 0.15);
        width: 32px;
        height: 4px;
        border-radius: 4px;
        padding: 0;
        cursor: pointer;
        transition: background-color 0.2s ease, width 0.2s ease;
      }

      .${CLASSES.dot}.${CLASSES.active} {
        background: #000;
      }

      @media screen and (max-width: 749px) {
        .${CLASSES.dot} {
          width: 22px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function getItemsPerView(slider, slides) {
    const sliderWidth = slider.clientWidth;
    const slideWidth = slides[0].getBoundingClientRect().width;

    if (!slideWidth) return 1;

    return Math.max(1, Math.round(sliderWidth / slideWidth));
  }

  function updateActiveDot(slider, slides, dots, itemsPerView) {
    const scrollLeft = slider.scrollLeft;
    let currentIndex = 0;

    slides.forEach((slide, index) => {
      // A slide "counts" as reached once the scroll position has passed
      // its left edge (with a small tolerance for sub-pixel rounding).
      if (slide.offsetLeft <= scrollLeft + 10) {
        currentIndex = index;
      }
    });

    const activePage = Math.min(dots.length - 1, Math.round(currentIndex / itemsPerView));

    dots.forEach((dot, index) => {
      dot.classList.toggle(CLASSES.active, index === activePage);
    });
  }

  function buildDots(gridSlider) {
    const slider = gridSlider.querySelector(SLIDER_SELECTOR);
    const allSlides = Array.from(gridSlider.querySelectorAll(SLIDE_SELECTOR));
    // Skip slides hidden via CSS (e.g. the mobile "Limit items shown" rule) —
    // dots should only ever count what's actually reachable right now.
    const slides = allSlides.filter((slide) => {
      const style = window.getComputedStyle(slide);
      return style.display !== 'none' && slide.offsetParent !== null;
    });

    if (!slider || slides.length < 2) return;

    // Remove any dots from a previous init (theme editor re-renders the
    // section on every settings change, so this keeps re-init safe).
    const existingDots = gridSlider.querySelector(`.${CLASSES.dots}`);
    if (existingDots) existingDots.remove();

    const itemsPerView = getItemsPerView(slider, slides);
    const totalPages = Math.max(1, Math.ceil(slides.length / itemsPerView));

    // Nothing to paginate if everything already fits on screen.
    if (totalPages <= 1) return;

    injectStyles();

    const dotsWrapper = document.createElement('div');
    dotsWrapper.className = CLASSES.dots;

    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = CLASSES.dot;
      dot.setAttribute('aria-label', 'Ir al grupo ' + (i + 1));

      dot.addEventListener('click', () => {
        const targetSlide = slides[i * itemsPerView];
        if (targetSlide) {
          slider.scrollTo({
            top: 0,
            left: targetSlide.offsetLeft,
            behavior: 'smooth',
          });
        }
      });

      dotsWrapper.appendChild(dot);
    }

    gridSlider.appendChild(dotsWrapper);

    const dots = Array.from(dotsWrapper.querySelectorAll(`.${CLASSES.dot}`));
    updateActiveDot(slider, slides, dots, itemsPerView);

    let scrollFrame = null;
    slider.addEventListener(
      'scroll',
      () => {
        if (scrollFrame) window.cancelAnimationFrame(scrollFrame);
        scrollFrame = window.requestAnimationFrame(() => {
          updateActiveDot(slider, slides, dots, itemsPerView);
        });
      },
      {passive: true}
    );
  }

  function init() {
    document.querySelectorAll(WRAPPER_SELECTOR).forEach(buildDots);
  }

  document.addEventListener('DOMContentLoaded', init);
  // Re-run when a section is (re)rendered in the theme editor.
  document.addEventListener('shopify:section:load', init);
  // Items-per-view depends on viewport width, so rebuild dots on resize.
  document.addEventListener('theme:resize:width', init);
})();